import { useReducer, useCallback } from 'react';

/** @typedef {'idle' | 'collect_prompt' | 'loading_questions' | 'needs_more_info' | 'loading_draft' | 'ready_preview' | 'error' | 'inserted'} Phase */

/**
 * @typedef {Object} SuggestedCode
 * @property {string} code - 'C1' | 'C2' | 'C3' | 'FI'
 * @property {number} confidence
 * @property {string} rationale
 */

/**
 * @typedef {Object} Draft
 * @property {SuggestedCode} suggested_code
 * @property {string} observation_text
 * @property {string} recommended_action
 * @property {string[]} reg_references
 * @property {string[]} [tags]
 * @property {string[]} [assumptions]
 * @property {boolean} [needs_review]
 */

/**
 * @typedef {Object} Question
 * @property {string} key
 * @property {string} prompt
 * @property {'boolean' | 'enum' | 'string' | 'multiselect'} type
 * @property {string[]} [options]
 * @property {boolean} required
 */

const PHASES = {
  IDLE: 'idle',
  COLLECT_PROMPT: 'collect_prompt',
  LOADING_QUESTIONS: 'loading_questions',
  NEEDS_MORE_INFO: 'needs_more_info',
  LOADING_DRAFT: 'loading_draft',
  READY_PREVIEW: 'ready_preview',
  ERROR: 'error',
  INSERTED: 'inserted',
};

const initialState = {
  phase: PHASES.IDLE,
  initialNote: '',
  questions: [],
  answers: {},
  draft: null,
  error: null,
  modelUsed: null,
  escalationRequested: false,
};

const RESET = 'RESET';
const OPEN = 'OPEN';
const SUBMIT_INITIAL_NOTE = 'SUBMIT_INITIAL_NOTE';
const RECEIVE_START_RESULT = 'RECEIVE_START_RESULT';
const RECEIVE_START_ERROR = 'RECEIVE_START_ERROR';
const SET_ANSWER = 'SET_ANSWER';
const SET_ANSWERS = 'SET_ANSWERS';
const SUBMIT_ANSWERS = 'SUBMIT_ANSWERS';
const RECEIVE_CONTINUE_RESULT = 'RECEIVE_CONTINUE_RESULT';
const RECEIVE_CONTINUE_ERROR = 'RECEIVE_CONTINUE_ERROR';
const INSERT = 'INSERT';
const BACK = 'BACK';
const RETRY = 'RETRY';

function reducer(state, action) {
  switch (action.type) {
    case RESET: {
      return { ...initialState };
    }

    case OPEN: {
      return {
        ...initialState,
        phase: PHASES.COLLECT_PROMPT,
      };
    }

    case SUBMIT_INITIAL_NOTE: {
      if (state.phase !== PHASES.COLLECT_PROMPT) return state;
      return {
        ...state,
        phase: PHASES.LOADING_QUESTIONS,
        initialNote: action.payload?.trim() ?? '',
      };
    }

    case RECEIVE_START_RESULT: {
      if (state.phase !== PHASES.LOADING_QUESTIONS) return state;
      const { status, questions = [], draft = null, modelUsed } = action.payload;
      if (status === 'needs_more_info') {
        return {
          ...state,
          phase: PHASES.NEEDS_MORE_INFO,
          questions,
          modelUsed: modelUsed ?? state.modelUsed,
          escalationRequested: false,
        };
      }
      if (status === 'ready_preview' && draft) {
        return {
          ...state,
          phase: PHASES.READY_PREVIEW,
          draft,
          questions: [],
          modelUsed: modelUsed ?? state.modelUsed,
          escalationRequested: false,
        };
      }
      return state;
    }

    case RECEIVE_START_ERROR: {
      if (state.phase !== PHASES.LOADING_QUESTIONS) return state;
      const err = action.payload;
      const msg =
        err?.message ??
        err?.response?.data?.error ??
        (typeof err === 'string' ? err : String(err ?? 'Unknown error'));
      return {
        ...state,
        phase: PHASES.ERROR,
        error: msg,
      };
    }

    case SET_ANSWER: {
      const { key, value } = action.payload;
      return {
        ...state,
        answers: { ...state.answers, [key]: value },
      };
    }

    case SET_ANSWERS: {
      return {
        ...state,
        answers: { ...state.answers, ...action.payload },
      };
    }

    case SUBMIT_ANSWERS: {
      if (state.phase !== PHASES.NEEDS_MORE_INFO) return state;
      return {
        ...state,
        phase: PHASES.LOADING_DRAFT,
      };
    }

    case RECEIVE_CONTINUE_RESULT: {
      if (state.phase !== PHASES.LOADING_DRAFT) return state;
      const { status, questions = [], draft = null, modelUsed } = action.payload;
      if (status === 'needs_more_info') {
        return {
          ...state,
          phase: PHASES.NEEDS_MORE_INFO,
          questions,
          modelUsed: modelUsed ?? state.modelUsed,
          escalationRequested: false,
        };
      }
      if (status === 'ready_preview' && draft) {
        return {
          ...state,
          phase: PHASES.READY_PREVIEW,
          draft,
          questions: [],
          modelUsed: modelUsed ?? state.modelUsed,
          escalationRequested: false,
        };
      }
      return state;
    }

    case RECEIVE_CONTINUE_ERROR: {
      if (state.phase !== PHASES.LOADING_DRAFT) return state;
      const err = action.payload;
      const msg =
        err?.message ??
        err?.response?.data?.error ??
        (typeof err === 'string' ? err : String(err ?? 'Unknown error'));
      return {
        ...state,
        phase: PHASES.ERROR,
        error: msg,
      };
    }

    case INSERT: {
      if (state.phase !== PHASES.READY_PREVIEW) return state;
      return {
        ...state,
        phase: PHASES.INSERTED,
      };
    }

    case BACK: {
      if (state.phase !== PHASES.READY_PREVIEW) return state;
      return {
        ...state,
        phase: PHASES.COLLECT_PROMPT,
        draft: null,
        questions: [],
        modelUsed: null,
        error: null,
        initialNote: state.initialNote,
        answers: state.answers,
      };
    }

    case RETRY: {
      if (state.phase !== PHASES.ERROR) return state;
      return {
        ...state,
        phase: PHASES.COLLECT_PROMPT,
        error: null,
        escalationRequested: Boolean(action.payload?.escalate),
        initialNote: state.initialNote,
        answers: state.answers,
      };
    }

    default:
      return state;
  }
}

/**
 * State machine hook for the AI Observation Helper multi-phase flow.
 * Manages phases: idle → collect_prompt → loading_questions → needs_more_info | ready_preview,
 * loading_draft → ready_preview | needs_more_info, with error and inserted states.
 *
 * API calls are performed by the consuming component; the hook receives results via
 * receiveStartResult / receiveContinueResult / receiveStartError / receiveContinueError.
 *
 * @returns {{
 *   phase: Phase,
 *   initialNote: string,
 *   questions: Question[],
 *   answers: Record<string, any>,
 *   draft: Draft | null,
 *   error: string | null,
 *   modelUsed: string | null,
 *   escalationRequested: boolean,
 *   open: () => void,
 *   reset: () => void,
 *   submitInitialNote: (note: string) => void,
 *   receiveStartResult: (result: { status: string, questions?: Question[], draft?: Draft, modelUsed?: string }) => void,
 *   receiveStartError: (err: any) => void,
 *   setAnswer: (key: string, value: any) => void,
 *   setAnswers: (updates: Record<string, any>) => void,
 *   submitAnswers: () => void,
 *   receiveContinueResult: (result: { status: string, questions?: Question[], draft?: Draft, modelUsed?: string }) => void,
 *   receiveContinueError: (err: any) => void,
 *   insert: () => void,
 *   back: () => void,
 *   retry: (opts?: { escalate?: boolean }) => void,
 *   isIdle: boolean,
 *   isCollectingPrompt: boolean,
 *   isLoadingQuestions: boolean,
 *   isNeedsMoreInfo: boolean,
 *   isLoadingDraft: boolean,
 *   isReadyPreview: boolean,
 *   isError: boolean,
 *   isInserted: boolean,
 *   shouldEscalate: boolean,
 * }}
 */
export function useAIObservationHelper() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const open = useCallback(() => dispatch({ type: OPEN }), []);
  const reset = useCallback(() => dispatch({ type: RESET }), []);
  const submitInitialNote = useCallback(
    (note) => dispatch({ type: SUBMIT_INITIAL_NOTE, payload: note }),
    []
  );
  const receiveStartResult = useCallback(
    (result) => dispatch({ type: RECEIVE_START_RESULT, payload: result }),
    []
  );
  const receiveStartError = useCallback(
    (err) => dispatch({ type: RECEIVE_START_ERROR, payload: err }),
    []
  );
  const setAnswer = useCallback(
    (key, value) => dispatch({ type: SET_ANSWER, payload: { key, value } }),
    []
  );
  const setAnswers = useCallback(
    (updates) => dispatch({ type: SET_ANSWERS, payload: updates }),
    []
  );
  const submitAnswers = useCallback(() => dispatch({ type: SUBMIT_ANSWERS }), []);
  const receiveContinueResult = useCallback(
    (result) => dispatch({ type: RECEIVE_CONTINUE_RESULT, payload: result }),
    []
  );
  const receiveContinueError = useCallback(
    (err) => dispatch({ type: RECEIVE_CONTINUE_ERROR, payload: err }),
    []
  );
  const insert = useCallback(() => dispatch({ type: INSERT }), []);
  const back = useCallback(() => dispatch({ type: BACK }), []);
  const retry = useCallback(
    (opts) => dispatch({ type: RETRY, payload: opts }),
    []
  );

  const p = state.phase;
  const confidence = state.draft?.suggested_code?.confidence ?? null;
  const shouldEscalate =
    state.escalationRequested ||
    (confidence !== null && typeof confidence === 'number' && confidence < 0.55);

  return {
    phase: state.phase,
    initialNote: state.initialNote,
    questions: state.questions,
    answers: state.answers,
    draft: state.draft,
    error: state.error,
    modelUsed: state.modelUsed,
    escalationRequested: state.escalationRequested,
    open,
    reset,
    submitInitialNote,
    receiveStartResult,
    receiveStartError,
    setAnswer,
    setAnswers,
    submitAnswers,
    receiveContinueResult,
    receiveContinueError,
    insert,
    back,
    retry,
    isIdle: p === PHASES.IDLE,
    isCollectingPrompt: p === PHASES.COLLECT_PROMPT,
    isLoadingQuestions: p === PHASES.LOADING_QUESTIONS,
    isNeedsMoreInfo: p === PHASES.NEEDS_MORE_INFO,
    isLoadingDraft: p === PHASES.LOADING_DRAFT,
    isReadyPreview: p === PHASES.READY_PREVIEW,
    isError: p === PHASES.ERROR,
    isInserted: p === PHASES.INSERTED,
    shouldEscalate,
  };
}

export { PHASES };
